"use client";

import React, { useState } from "react";
import { Copy, Check } from "lucide-react";
import { globalToast } from "./ToastProvider";

interface CopyButtonProps {
  text: string;
  label?: string;
  successMessage?: string;
  showToast?: boolean;
  className?: string;
  variant?: "icon" | "pill" | "subtle";
}

export function CopyButton({
  text,
  label,
  successMessage = "Copied to clipboard!",
  showToast = true,
  className = "",
  variant = "pill",
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);

      if (showToast) {
        globalToast.success(successMessage, { duration: 2500 });
      }

      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (variant === "icon") {
    return (
      <button
        type="button"
        onClick={handleCopy}
        aria-label={copied ? "Copied" : `Copy ${label || text}`}
        className={`p-1.5 rounded-lg text-slate-400 hover:text-slate-800 dark:hover:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 active:scale-90 transition-all cursor-pointer ${className}`}
        title={copied ? "Copied!" : `Copy ${label || text}`}
      >
        {copied ? (
          <Check className="w-4 h-4 text-emerald-500 animate-in zoom-in-75 duration-200" />
        ) : (
          <Copy className="w-4 h-4" />
        )}
      </button>
    );
  }

  if (variant === "subtle") {
    return (
      <button
        type="button"
        onClick={handleCopy}
        className={`inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-blue-600 dark:hover:text-cyan-400 transition-colors cursor-pointer ${className}`}
      >
        {copied ? (
          <Check className="w-3.5 h-3.5 text-emerald-500 animate-in zoom-in duration-150" />
        ) : (
          <Copy className="w-3.5 h-3.5" />
        )}
        <span>{copied ? "Copied!" : label || "Copy"}</span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={copied ? "Copied to clipboard" : `Copy ${label || text}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border transition-all cursor-pointer active:scale-95 ${
        copied
          ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400 shadow-sm"
          : "bg-slate-100 dark:bg-slate-800/80 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
      } ${className}`}
    >
      {copied ? (
        <Check className="w-3.5 h-3.5 text-emerald-500 animate-in zoom-in-75 duration-150" />
      ) : (
        <Copy className="w-3.5 h-3.5 text-slate-400" />
      )}
      <span>{copied ? "Copied!" : label || "Copy"}</span>
    </button>
  );
}
